import { NodeElement } from 'rc-tree/lib/interface';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import DataTable, {
  TableProps,
  PaginationComponentProps,
} from 'react-data-table-component';
import { PaginateOptions } from '../services/types';
import { FilterSelect, FilterSelectOption } from './inputs/filter-select';
import { Sprite } from './sprite';

interface Props<T> extends TableProps<T> {
  currentPage: number;
  serverAction: { (input: PaginateOptions): Promise<unknown> };
  defaultOrderReference?: string;
  searchBarPlaceholder?: string;
  sortByOptions?: FilterSelectOption[];
  filterBy?: string;
  filterByOptions?: FilterSelectOption[];
  filterComponent?: React.ReactNode;
  hideRefresh?: boolean;
  rightFilterComponent?: NodeElement;
}

const CustomPaginationComponent: React.FC<PaginationComponentProps> = ({
  rowsPerPage,
  rowCount,
  currentPage,
  onChangePage,
  onChangeRowsPerPage,
}) => {
  const perPageOptions = useMemo(
    () =>
      [10, 15, 20, 25, 30].map(val => ({
        value: val.toString(),
        label: val.toString(),
      })),
    [],
  );
  const lastPage = useMemo(
    () => Math.ceil(rowCount / rowsPerPage),
    [rowCount, rowsPerPage],
  );

  return (
    <div className="flex flex-row justify-between mt-[20px]">
      <div className="flex flex-row">
        <div className="flex flex-col justify-center text-gray-dark font-semibold text-sm">
          Show
        </div>
        <div className="flex flex-col justify-center px-3">
          <FilterSelect
            currentValue={rowsPerPage.toString()}
            options={perPageOptions}
            onChange={value => onChangeRowsPerPage(Number(value), currentPage)}
          />
        </div>
        <div className="flex flex-col justify-center text-gray-dark text-sm">
          items per page
        </div>
      </div>
      <div className="flex flex-row">
        <button
          type="button"
          onClick={() => onChangePage(1, rowCount)}
          disabled={currentPage === 1}
          className="p-2.5 bg-gray-medium enabled:hover:bg-blue-light rounded-[8px] text-blue fill-blue disabled:text-gray disabled:fill-gray">
          <Sprite name="icon-first-page" className="w-[20px] h-[20px]" />
        </button>
        <button
          type="button"
          onClick={() => onChangePage(currentPage - 1, rowCount)}
          disabled={currentPage - 1 < 1}
          className="p-2.5 bg-gray-medium enabled:hover:bg-blue-light rounded-[8px] ml-2 text-blue fill-blue disabled:text-gray disabled:fill-gray">
          <Sprite name="icon-prev-page" className="w-[20px] h-[20px]" />
        </button>
        <button
          type="button"
          className="py-2 px-[14px] bg-blue rounded-[8px] text-white mx-2">
          {currentPage}
        </button>
        <button
          type="button"
          onClick={() => onChangePage(currentPage + 1, rowCount)}
          disabled={currentPage >= lastPage}
          className="p-2.5 bg-gray-medium enabled:hover:bg-blue-light rounded-[8px] mr-2 text-blue fill-blue disabled:text-gray disabled:fill-gray">
          <Sprite name="icon-next-page" className="w-[20px] h-[20px]" />
        </button>
        <button
          type="button"
          onClick={() => onChangePage(lastPage, rowCount)}
          disabled={currentPage === lastPage}
          className="p-2.5 bg-gray-medium enabled:hover:bg-blue-light rounded-[8px] text-blue fill-blue disabled:text-gray disabled:fill-gray">
          <Sprite name="icon-last-page" className="w-[20px] h-[20px]" />
        </button>
      </div>
    </div>
  );
};

interface Context {
  currentPage: number;
  searchBarPlaceholder?: string;
  sortByOptions?: FilterSelectOption[];
  filterBy?: string;
  filterByOptions?: FilterSelectOption[];
  hideRefresh: boolean;
  paginationPerPage: number;
  setPaginationPerPage: { (state: number): void };
  orderBy: string;
  setOrderBy: { (state: string): void };
  orderDirection: string;
  setOrderDirection: { (state: string): void };
  searchTerm?: string;
  setSearchTerm: { (state: string): void };
  filterByValue: string;
  setFilterByValue: { (state: string): void };
  filterByField: string;
  setFilterByField: { (state: string): void };
  getServerArgs: { (args: { page: number }): PaginateOptions };
  query: { (args: { page: number }): void };
}

const ServerSideDatatableContext = createContext<Context | undefined>(
  undefined,
);

export const useServerSideDatatable = () => {
  const context = useContext(ServerSideDatatableContext);
  if (!context) {
    throw new Error(
      'useServerSideDatatable must be used inside a ServerSideDatatableContext',
    );
  }
  return context;
};

export const ServerSideDatatable = <T,>({
  searchBarPlaceholder,
  currentPage,
  serverAction,
  defaultOrderReference,
  sortByOptions,
  filterBy,
  filterByOptions,
  filterComponent,
  hideRefresh = false,
  rightFilterComponent,
  ...props
}: Props<T>) => {
  const [paginationPerPage, setPaginationPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<string>(defaultOrderReference || '');
  const [orderDirection, setOrderDirection] = useState('DESC');
  const [searchTerm, setSearchTerm] = useState<string | undefined>();
  const [filterByValue, setFilterByValue] = useState<string>('');
  const [filterByField, setFilterByField] = useState<string>('');

  const getServerArgs = useCallback(
    ({ page }: { page: number }): PaginateOptions => {
      let args: PaginateOptions = {
        page,
        offset: paginationPerPage,
        orderBy,
        orderDirection,
        filterByValue,
        filterByField,
      };
      if (searchTerm) {
        args = { ...args, searchTerm };
      }
      return args;
    },
    [
      searchTerm,
      paginationPerPage,
      orderBy,
      orderDirection,
      filterByValue,
      filterByField,
    ],
  );

  const handleSort = (column: any, sortDirection: string) => {
    if (column?.sortField && sortDirection) {
      setOrderBy(column.sortField);
      setOrderDirection(sortDirection);
      query({ page: currentPage });
    }
  };

  const query = useCallback(
    ({ page }: { page: number }) => {
      const args = getServerArgs({ page });
      serverAction(args);
    },
    [getServerArgs, serverAction],
  );

  useEffect(() => {
    query({ page: 1 });
  }, [query]);
  const providerValue = useMemo(
    () => ({
      searchBarPlaceholder,
      currentPage,
      sortByOptions,
      filterByOptions,
      hideRefresh,
      paginationPerPage,
      setPaginationPerPage,
      orderBy,
      setOrderBy,
      orderDirection,
      setOrderDirection,
      searchTerm,
      setSearchTerm,
      filterByValue,
      setFilterByValue,
      filterByField,
      setFilterByField,
      getServerArgs,
      query,
    }),
    [
      searchBarPlaceholder,
      currentPage,
      sortByOptions,
      filterByOptions,
      hideRefresh,
      paginationPerPage,
      setPaginationPerPage,
      orderBy,
      setOrderBy,
      orderDirection,
      setOrderDirection,
      searchTerm,
      setSearchTerm,
      filterByValue,
      setFilterByValue,
      filterByField,
      setFilterByField,
      getServerArgs,
      query,
    ],
  );

  return (
    <ServerSideDatatableContext.Provider value={providerValue}>
      {filterComponent || (
        <div className="flex flex-row mb-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute top-0 pl-[14.5px] h-full flex flex-col justify-center z-[2] pointer-events-none">
                <Sprite
                  name="icon-search"
                  className="w-[20px] h-[20px] text-gray fill-gray"
                />
              </div>
              <input
                type="text"
                className="border border-gray-medium rounded-md pl-[40px] py-2 pr-2 placeholder:text-gray relative z-[1] w-full bg-white"
                placeholder={searchBarPlaceholder}
                onChange={event => setFilterByValue(event.currentTarget.value)}
              />
            </div>
          </div>
          <div className="flex-1 flex flex-row justify-end pl-16">
            <div className="flex flex-row">
              {sortByOptions && (
                <div className="w-[184px]">
                  <FilterSelect
                    currentValue={orderBy}
                    placeholder="Sort by"
                    options={sortByOptions}
                    onChange={value => setOrderBy(value)}
                  />
                </div>
              )}
              {rightFilterComponent}
              {filterByOptions && (
                <div className="w-[184px] pl-3">
                  <FilterSelect
                    currentValue={filterByValue}
                    placeholder="All"
                    options={filterByOptions}
                    onChange={value => {
                      if (filterBy) {
                        setFilterByField(filterBy);
                      }
                      setFilterByValue(value);
                    }}
                    icon={
                      <Sprite
                        name="icon-caret"
                        className="w-[20px] h-[20px] text-gray fill-gray"
                      />
                    }
                  />
                </div>
              )}
            </div>
            {!hideRefresh && (
              <div className="pl-16">
                <button
                  type="button"
                  onClick={() => query({ page: currentPage })}
                  className="px-4 py-2 border-[2px] border-white bg-white shadow-[0px_1px_1px_rgba(0,0,0,0.06)] active:shadow-none rounded-[5px] font-semibold text-blue">
                  Refresh
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      <DataTable
        className="card-shadow"
        highlightOnHover
        pagination
        paginationServer
        sortServer
        onSort={handleSort}
        paginationDefaultPage={1}
        paginationPerPage={paginationPerPage}
        paginationComponent={CustomPaginationComponent}
        onChangePage={page => query({ page })}
        onChangeRowsPerPage={currentRowsPerPage => {
          setPaginationPerPage(currentRowsPerPage);
          query({ page: currentPage });
        }}
        customStyles={{
          tableWrapper: {
            style: {
              overflow: 'scroll',
              minWidth: '1024px',
            },
          },
          headRow: {
            style: {
              borderBottom: '4px solid #EEEEEE',
            },
          },
          rows: {
            style: {
              paddingTop: '1rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid #EEEEEE',
            },
          },
        }}
        {...props}
      />
    </ServerSideDatatableContext.Provider>
  );
};

ServerSideDatatable.defaultProps = {
  sortByOptions: undefined,
  filterBy: undefined,
  filterByOptions: undefined,
  defaultOrderReference: '',
  filterComponent: undefined,
  searchBarPlaceholder: 'Search',
  hideRefresh: false,
};
